<script setup>
/**
 * Batch Add Apple Links — parses a CSV (code,link columns) and registers each
 * link via /users/registerCodes. Mirrors Vue2's ManageAppleLinks.vue.
 */
import { ref } from "vue";
import { useRouter } from "vue-router";
import { registerAppleLink } from "./api";

// Cap concurrent in-flight requests — firing one request per row all at once
// (Promise.all over the whole array) would open thousands of simultaneous
// connections for large CSVs and hang the tab / overwhelm the backend.
const CONCURRENCY = 10;

const router = useRouter();
const csvFileName = ref(""), rows = ref([]), loading = ref(false), success = ref(false), error = ref("");
const processedCount = ref(0), failedCount = ref(0);

function parseCsv(text) {
  const lines = text.trim().split(/\r?\n/);
  const headers = lines[0].split(",").map(h => h.replace(/^\uFEFF/, "").trim().toLowerCase());
  return lines.slice(1).map(line => {
    const vals = line.split(",");
    return Object.fromEntries(headers.map((h, i) => [h, (vals[i] ?? "").trim()]));
  }).filter(r => r.code);
}

function onFile(e) {
  const file = e.target.files?.[0]; e.target.value = "";
  if (!file) return;
  const reader = new FileReader();
  reader.onload = (ev) => { rows.value = parseCsv(ev.target.result); csvFileName.value = file.name; };
  reader.readAsText(file);
}

// Runs `worker` over `items` with at most `concurrency` requests in flight at
// once. Individual failures are counted rather than aborting the whole batch,
// so one bad row doesn't stop the remaining thousands from being processed.
async function processWithConcurrency(items, worker, concurrency) {
  let index = 0, failed = 0;
  async function runNext() {
    while (index < items.length) {
      const item = items[index++];
      try { await worker(item); }
      catch { failed++; }
      finally { processedCount.value++; }
    }
  }
  const workers = Array.from({ length: Math.min(concurrency, items.length) }, runNext);
  await Promise.all(workers);
  return failed;
}

async function submit() {
  error.value = ""; success.value = false;
  if (!csvFileName.value) { error.value = "Please include a csv file."; return; }
  loading.value = true;
  processedCount.value = 0;
  failedCount.value = 0;
  try {
    failedCount.value = await processWithConcurrency(
      rows.value,
      (r) => registerAppleLink({ code: r.code, link: r.link }),
      CONCURRENCY,
    );
    if (failedCount.value > 0) {
      error.value = `${failedCount.value} of ${rows.value.length} row(s) failed to register. Please check and retry.`;
    } else {
      success.value = true;
    }
  } catch { error.value = "Error occurred. Please try again."; }
  finally { loading.value = false; }
}
</script>

<template>
  <section>
    <VBtn variant="text" prepend-icon="mdi:arrow-left" class="mb-4" @click="router.push({ name: 'users' })">Back</VBtn>
    <h1 class="text-h5 mb-4">Batch Add Apple Links</h1>
    <VCard class="pa-4" max-width="560">
      <VAlert v-if="error" type="error" class="mb-4">{{ error }}</VAlert>
      <VAlert v-if="success" type="success" class="mb-4">Batch add/update successful.</VAlert>
      <div class="mb-4">
        <div class="text-body-2 font-weight-semibold mb-1">CSV File</div>
        <input type="file" accept=".csv" @change="onFile" />
        <p v-if="csvFileName" class="text-caption text-medium-emphasis mt-1">{{ csvFileName }} — {{ rows.length }} row(s)</p>
      </div>
      <div v-if="loading" class="mb-4">
        <VProgressLinear :model-value="(processedCount / rows.length) * 100" height="8" rounded />
        <p class="text-caption text-medium-emphasis mt-1">{{ processedCount }} / {{ rows.length }} processed</p>
      </div>
      <VBtn :loading="loading" :disabled="!csvFileName" @click="submit">Submit</VBtn>
    </VCard>
  </section>
</template>
